import { fabric } from 'fabric';
import { Observable, Subscription } from 'rxjs';
import { Point } from 'src/app/models/session-sync';
import { isRenderableCanvas } from 'src/app/services/session-rtd.service';
import { ColorUtils } from 'src/app/utilities/color.utils';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';

enum MagicInkPhase {
  Drawing = 'drawing',
  Holding = 'holding',
  Fading = 'fading',
  Idle = 'idle',
}

const MAGIC_INK_RADIUS = 4; // Default radius in canvas space at zoom 1
const MAGIC_INK_SHADOW_BLUR = 25; // Default glow blur in canvas space at zoom 1
const MAGIC_INK_COLOR = '#F80000'; // ink color
const MAGIC_INK_CORE_COLOR = '#FFF4F5'; // core color

export const MAGIC_INK_CONFIG_DEFAULT = {
  fadeDelayMs: 400,
  fadeDurationMs: 500,
  minDistanceBetweenPoints: 3,
};

let MAGIC_INK_CONFIG = {
  fadeDelayMs: MAGIC_INK_CONFIG_DEFAULT.fadeDelayMs, // how long the full-opacity drawing persists after mouse-up before fading starts
  fadeDurationMs: MAGIC_INK_CONFIG_DEFAULT.fadeDurationMs, // how long the fade-out transition takes once it begins
  minDistanceBetweenPoints: MAGIC_INK_CONFIG_DEFAULT.minDistanceBetweenPoints, // minimum distance between points in a segment
};

// Glow trail (outer stroke)
const MAGIC_INK_GLOW_TRAIL_STROKE_OPACITY = 0.95;
const MAGIC_INK_GLOW_TRAIL_SHADOW_OPACITY = 0.8;

// Core trail (inner bright stroke)
const MAGIC_INK_CORE_TRAIL_WIDTH_RATIO = 0.22;
const MAGIC_INK_CORE_TRAIL_STROKE_OPACITY = 0.95;

// Head outer halo (single-point glow)
const MAGIC_INK_HEAD_OUTER_HALO_FILL_OPACITY = 0.28;
const MAGIC_INK_HEAD_OUTER_HALO_SHADOW_BLUR_RATIO = 1.2;
const MAGIC_INK_HEAD_OUTER_HALO_SHADOW_OPACITY = 0.8;
const MAGIC_INK_HEAD_OUTER_HALO_RADIUS_RATIO = 1.35;

// Head mid ring
const MAGIC_INK_HEAD_MID_RING_FILL_OPACITY = 0.9;
const MAGIC_INK_HEAD_MID_RING_RADIUS_RATIO = 0.9;

// Head core dot
const MAGIC_INK_HEAD_CORE_DOT_RADIUS_RATIO = 0.4;

interface RgbColor {
  r: number;
  g: number;
  b: number;
}

// Returns the point radius scaled inversely with zoom so it stays a constant screen size as you zoom in/out
export function getMagicInkPointRadius(canvas?: fabric.StaticCanvas): number {
  const zoom = canvas?.getZoom() ?? 1;
  return MAGIC_INK_RADIUS / Math.max(zoom, Number.EPSILON);
}

export function setupMagicInkConfig(config: typeof MAGIC_INK_CONFIG): void {
  MAGIC_INK_CONFIG = config;
}

/**
 * A single "magic ink" stroke segment — a polyline captured while the mouse was held down.
 */
export class MagicInkSegment {
  readonly points: Point[] = [];

  constructor(initialPoint: Point) {
    this.points.push(initialPoint);
  }

  addPoint(point: Point): void {
    this.points.push(point);
  }

  get lastPoint(): Point | undefined {
    return this.points[this.points.length - 1];
  }
}

/**
 * Fabric.js object that renders all accumulated magic-ink segments with a shared global
 * opacity. Supports the same glowing head + Catmull-Rom trail style.
 */
export class MagicInkTrail extends fabric.Object {
  private readonly colorRgb: RgbColor;
  private readonly coreColorRgb: RgbColor;
  private readonly segments: MagicInkSegment[] = [];
  private activeSegment: MagicInkSegment | undefined;
  private _globalOpacity = 1;

  constructor() {
    super({
      left: 0,
      top: 0,
      width: 0,
      height: 0,
      selectable: false,
      evented: false,
      objectCaching: false,
    });
    this.colorRgb = ColorUtils.hexToRgb(MAGIC_INK_COLOR);
    this.coreColorRgb = ColorUtils.hexToRgb(MAGIC_INK_CORE_COLOR);
  }

  /**
   * Always report as on-screen so Fabric never culls this overlay object based on its bounding box.
   */
  isOnScreen(): boolean {
    return true;
  }

  set globalOpacity(value: number) {
    this._globalOpacity = Math.max(0, Math.min(1, value));
  }

  // starts a new active segment
  startSegment(point: Point): void {
    this.activeSegment = new MagicInkSegment(point);
  }

  // appends to the active segment
  addPoint(point: Point): boolean {
    if (this.activeSegment && !this.skipAddingPoint(point)) {
      this.activeSegment.addPoint(point);
      return true;
    }
    return false;
  }

  private skipAddingPoint(point: Point): boolean {
    const prevPoint = this.activeSegment?.lastPoint;
    if (!prevPoint) {
      return false;
    }
    const distance = Math.abs(point.x - prevPoint.x) + Math.abs(point.y - prevPoint.y);
    return distance < MAGIC_INK_CONFIG.minDistanceBetweenPoints;
  }

  // commits the active segment to the segments list
  commitSegment(point?: Point): void {
    if (!this.activeSegment) {
      return;
    }
    if (point && !this.skipAddingPoint(point)) {
      this.activeSegment.addPoint(point);
    }
    if (this.activeSegment.points.length > 0) {
      this.segments.push(this.activeSegment);
    }
    this.activeSegment = undefined;
  }

  public _render(ctx: CanvasRenderingContext2D): void {
    const opacity = this._globalOpacity;
    if (opacity <= 0) {
      return;
    }

    const radius = getMagicInkPointRadius(this.canvas);
    const shadowBlur = MAGIC_INK_SHADOW_BLUR;
    const width = radius * 2;

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // Draw all committed segments
    for (const segment of this.segments) {
      this.drawSegment(ctx, segment.points, radius, shadowBlur, width, opacity);
    }

    // Draw the currently active (in-progress) segment
    if (this.activeSegment && this.activeSegment.points.length > 0) {
      this.drawSegment(ctx, this.activeSegment.points, radius, shadowBlur, width, opacity);
    }

    ctx.restore();
  }

  private drawSegment(
    ctx: CanvasRenderingContext2D,
    pts: Point[],
    radius: number,
    shadowBlur: number,
    width: number,
    opacity: number,
  ): void {
    if (pts.length === 0) {
      return;
    }

    if (pts.length > 1) {
      // Outer glow trail
      ctx.lineWidth = width;
      ctx.strokeStyle = this.getColor(this.colorRgb, opacity * MAGIC_INK_GLOW_TRAIL_STROKE_OPACITY);
      ctx.shadowBlur = shadowBlur;
      ctx.shadowColor = this.getColor(this.colorRgb, opacity * MAGIC_INK_GLOW_TRAIL_SHADOW_OPACITY);
      ctx.beginPath();
      this.buildSplinePath(ctx, pts);
      ctx.stroke();

      // Bright core trail
      ctx.lineWidth = width * MAGIC_INK_CORE_TRAIL_WIDTH_RATIO;
      ctx.strokeStyle = this.getColor(
        this.coreColorRgb,
        opacity * MAGIC_INK_CORE_TRAIL_STROKE_OPACITY,
      );
      ctx.beginPath();
      this.buildSplinePath(ctx, pts);
      ctx.stroke();
    }

    // Glowing head only when the segment is a single point (no trail yet)
    if (pts.length === 1) {
      this.drawHead(ctx, pts[0], radius, shadowBlur, opacity);
    }
  }

  private drawHead(
    ctx: CanvasRenderingContext2D,
    headPoint: Point,
    radius: number,
    shadowBlur: number,
    opacity: number,
  ): void {
    ctx.fillStyle = this.getColor(this.colorRgb, opacity * MAGIC_INK_HEAD_OUTER_HALO_FILL_OPACITY);
    ctx.shadowBlur = shadowBlur * MAGIC_INK_HEAD_OUTER_HALO_SHADOW_BLUR_RATIO;
    ctx.shadowColor = this.getColor(
      this.colorRgb,
      opacity * MAGIC_INK_HEAD_OUTER_HALO_SHADOW_OPACITY,
    );
    ctx.beginPath();
    ctx.arc(
      headPoint.x,
      headPoint.y,
      radius * MAGIC_INK_HEAD_OUTER_HALO_RADIUS_RATIO,
      0,
      Math.PI * 2,
    );
    ctx.fill();

    ctx.fillStyle = this.getColor(this.colorRgb, opacity * MAGIC_INK_HEAD_MID_RING_FILL_OPACITY);
    ctx.beginPath();
    ctx.arc(
      headPoint.x,
      headPoint.y,
      radius * MAGIC_INK_HEAD_MID_RING_RADIUS_RATIO,
      0,
      Math.PI * 2,
    );
    ctx.fill();

    ctx.fillStyle = this.getColor(this.coreColorRgb, opacity);
    ctx.beginPath();
    ctx.arc(
      headPoint.x,
      headPoint.y,
      radius * MAGIC_INK_HEAD_CORE_DOT_RADIUS_RATIO,
      0,
      Math.PI * 2,
    );
    ctx.fill();
  }

  // to get a smooth, continuous curve through the points
  private buildSplinePath(ctx: CanvasRenderingContext2D, pts: Point[]): void {
    if (pts.length < 2) {
      return;
    }

    ctx.moveTo(pts[0].x, pts[0].y);

    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(i + 2, pts.length - 1)];

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y);
    }
  }

  private getColor(color: RgbColor, opacity: number): string {
    const alpha = Math.max(0, Math.min(1, opacity));
    return `rgba(${color.r},${color.g},${color.b},${alpha})`;
  }
}

/**
 * Manages the lifecycle of a single persistent MagicInkTrail:
 *
 * - While the mouse is held: phase = 'drawing' — points accumulate, opacity = 1.
 * - On mouse-up: phase = 'holding' — a delay timer starts (fadeDelayMs). Opacity stays at 1.
 *   If the mouse goes down again before the delay expires the timer is cancelled, all
 *   existing ink is kept, opacity resets to 1, and we return to 'drawing'.
 * - After the delay: phase = 'fading' — rAF loop decrements globalOpacity over fadeDurationMs.
 * - When opacity reaches 0: phase = 'idle' — trail is cleared and removed from canvas.
 */
export class MagicInkTrailAnimator {
  private trail: MagicInkTrail | undefined;
  private phase: MagicInkPhase = MagicInkPhase.Idle;
  private fadeDelayTimeoutId: ReturnType<typeof modifiedSetTimeout> | undefined;
  private animationFrameId: number | undefined;
  private fadeStartTime: number | undefined;
  private overlayCanvasChangesSubscription?: Subscription;

  constructor(
    private overlayCanvas?: fabric.StaticCanvas,
    overlayCanvasChanges$?: Observable<fabric.StaticCanvas | undefined>,
  ) {
    this.overlayCanvasChangesSubscription = overlayCanvasChanges$?.subscribe(
      (updatedOverlayCanvas) => {
        this.setOverlayCanvas(updatedOverlayCanvas);
      },
    );
  }

  setOverlayCanvas(overlayCanvas: fabric.StaticCanvas | undefined): boolean {
    if (this.overlayCanvas === overlayCanvas) {
      return this.hasOverlayCanvas;
    }

    this.clear();
    this.overlayCanvas = overlayCanvas;
    return this.hasOverlayCanvas;
  }

  get hasOverlayCanvas(): boolean {
    return isRenderableCanvas(this.overlayCanvas);
  }

  /** Call on mouse-down */
  startStroke(point: Point): void {
    const overlayCanvas = this.getOverlayCanvas();
    if (!overlayCanvas) {
      return;
    }

    this.cancelFadeDelay();
    this.cancelAnimationFrame();

    if (this.trail) {
      // Resume drawing on existing visible ink — reset opacity so everything pops back to full
      this.trail.globalOpacity = 1;
    } else {
      this.trail = new MagicInkTrail();
      overlayCanvas.add(this.trail);
    }

    this.trail.startSegment(point);
    this.phase = MagicInkPhase.Drawing;
    this.renderCanvas();
  }

  /** Call on mouse-move (only while drawing) */
  addPoint(point: Point): void {
    if (this.phase !== MagicInkPhase.Drawing || !this.trail) {
      return;
    }
    if (this.trail.addPoint(point)) {
      this.renderCanvas();
    }
  }

  /** Call on mouse-up */
  endStroke(point?: Point): void {
    if (this.phase !== MagicInkPhase.Drawing || !this.trail) {
      return;
    }
    this.trail.commitSegment(point);
    this.phase = MagicInkPhase.Holding;
    this.renderCanvas();

    this.fadeDelayTimeoutId = modifiedSetTimeout(
      () => {
        this.fadeDelayTimeoutId = undefined;
        this.beginFade();
      },
      MAGIC_INK_CONFIG.fadeDelayMs,
      true,
    );
  }

  /** Removes the trail immediately and resets all state */
  clear(): void {
    this.cancelFadeDelay();
    this.cancelAnimationFrame();
    this.clearTrail();
    this.phase = MagicInkPhase.Idle;
    this.renderCanvas();
  }

  destroy(): void {
    this.overlayCanvasChangesSubscription?.unsubscribe();
    this.overlayCanvasChangesSubscription = undefined;
    this.clear();
  }

  renderCanvas(): void {
    this.getOverlayCanvas()?.requestRenderAll();
  }

  get isActivelyDrawing(): boolean {
    return this.phase === MagicInkPhase.Drawing;
  }

  private beginFade(): void {
    if (!this.trail) {
      return;
    }
    this.phase = MagicInkPhase.Fading;
    this.fadeStartTime = performance.now();
    this.scheduleFrame();
  }

  private scheduleFrame(): void {
    if (this.animationFrameId !== undefined) {
      return;
    }
    this.animationFrameId = globalThis.requestAnimationFrame(() => {
      this.animationFrameId = undefined;
      this.renderFadeFrame();
    });
  }

  private renderFadeFrame(): void {
    if (this.phase !== MagicInkPhase.Fading || !this.trail || this.fadeStartTime === undefined) {
      return;
    }

    const elapsed = performance.now() - this.fadeStartTime;
    const progress = Math.min(elapsed / MAGIC_INK_CONFIG.fadeDurationMs, 1);
    this.trail.globalOpacity = 1 - progress;
    this.renderCanvas();

    if (progress < 1) {
      this.scheduleFrame();
    } else {
      // Fade complete — clean up
      this.clearTrail();
      this.phase = MagicInkPhase.Idle;
      this.renderCanvas();
    }
  }

  private clearTrail(): void {
    if (!this.trail) {
      return;
    }
    this.getOverlayCanvas()?.remove(this.trail);
    this.trail = undefined;
  }

  private getOverlayCanvas(): fabric.StaticCanvas | undefined {
    return isRenderableCanvas(this.overlayCanvas) ? this.overlayCanvas : undefined;
  }

  private cancelFadeDelay(): void {
    if (this.fadeDelayTimeoutId !== undefined) {
      clearTimeout(this.fadeDelayTimeoutId);
      this.fadeDelayTimeoutId = undefined;
    }
  }

  private cancelAnimationFrame(): void {
    if (this.animationFrameId !== undefined) {
      globalThis.cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = undefined;
    }
    this.fadeStartTime = undefined;
  }

  public terminateDrawing(): void {
    if (this.phase === MagicInkPhase.Drawing) {
      this.endStroke();
    }
  }
}
